// src/pages/LoginPage.tsx

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { loginRateLimiter } from '../utils/rateLimiter';

const ALLOWED_DOMAIN = '@shelistens.com.br';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Captura a URL de redirecionamento da query string
  const redirectTo = searchParams.get('redirect') || '/';

  // Identificador para rate limiting (usa combinação de session + fingerprint)
  const getRateLimitKey = () => {
    const sessionKey = sessionStorage.getItem('rate_limit_key');
    if (sessionKey) return sessionKey;

    // Gerar identificador único para esta sessão
    const newKey = `login_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('rate_limit_key', newKey);
    return newKey;
  };

  const handleGoogleLogin = async () => {
    setError('');

    // Verificar rate limit ANTES de fazer qualquer operação
    const rateLimitKey = getRateLimitKey();
    if (!loginRateLimiter.isAllowed(rateLimitKey)) {
      const remaining = loginRateLimiter.getRemainingAttempts(rateLimitKey);
      const waitTime = Math.ceil(loginRateLimiter.getTimeUntilReset(rateLimitKey) / 1000);

      setError(
        `Muitas tentativas de login. Aguarde ${waitTime} segundos antes de tentar novamente. ` +
        `(${remaining} tentativas restantes)`
      );
      return;
    }

    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      
      // Hint de domínio para o Google (sugere contas corporativas)
      provider.setCustomParameters({
        hd: 'shelistens.com.br',
        prompt: 'select_account'
      });

      // Login com popup
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const userEmail = user.email;

      console.log('[AUDIT] Tentativa de login:', {
        email: userEmail,
        emailVerified: user.emailVerified,
        timestamp: new Date().toISOString()
      });

      // VALIDAÇÃO 1: Email verificado (conforme estória)
      if (!user.emailVerified) {
        console.warn('[AUDIT] Login negado - email não verificado:', userEmail);
        await signOut(auth);
        setError('Email não verificado. Verifique seu email antes de fazer login.');
        setLoading(false);
        return;
      }

      // VALIDAÇÃO 2: Domínio corporativo @shelistens.com.br
      if (!userEmail?.endsWith(ALLOWED_DOMAIN)) {
        console.warn('[AUDIT] Login negado - domínio não autorizado:', {
          email: userEmail,
          domain: userEmail?.split('@')[1],
          allowedDomain: ALLOWED_DOMAIN,
          reason: 'domain_restricted'
        });
        
        await signOut(auth);
        setError(
          `Acesso restrito a colaboradores SHE (${ALLOWED_DOMAIN}). A conta ${userEmail} não tem permissão para acessar este ambiente.`
        );
        setLoading(false);
        return;
      }

      // Login aprovado
      console.log('[AUDIT] Login aprovado:', {
        email: userEmail,
        uid: user.uid,
        action: 'login_success',
        redirectTo,
        timestamp: new Date().toISOString()
      });

      // Redireciona para a rota original ou para a home
      navigate(redirectTo);
    } catch (err: any) {
      console.error('[AUDIT] Erro no login:', {
        error: err.code,
        message: err.message,
        timestamp: new Date().toISOString()
      });

      // Mensagens de erro específicas
      if (err.code === 'auth/popup-blocked') {
        setError('Popup bloqueado. Permita popups para este site e tente novamente.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('Login cancelado. Tente novamente.');
      } else {
        setError('Erro ao fazer login com Google. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-she px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 border-2 border-she-palido-300">
        {/* Logo SHE */}
        <div className="flex justify-center mb-6">
          <svg
            className="w-20 h-19"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 453.9 428.6"
          >
            <g>
              <path
                fill="#8e162e"
                d="M267,67.3c2.5,3.2,5.8,5.7,9.5,7.1,31.4,12.3,57.6,35,74.5,63.7,1.6,2.7,4.4,4.4,7.5,4.4h0c6.7,0,10.9-7.3,7.5-13.1-22.8-38.6-60.5-67.3-105.2-78.1-2.6-.6-4.5,2.4-2.9,4.4l9,11.5Z"
              />
              <path
                fill="#8e162e"
                d="M176.3,360.6c-2.1-2.7-4.9-4.6-8.1-5.9-29.6-11.3-54.8-31.7-72-57.8-1.6-2.5-4.4-4-7.3-4h0c-6.9,0-11.1,7.6-7.3,13.4,23.2,35.2,59.3,61.2,101.6,71.2s4.5-2.4,2.9-4.5l-9.7-12.4Z"
              />
              <path
                fill="#8e162e"
                d="M347,296.9c-17.7,26.8-43.7,47.6-74.4,58.7-1.1.4-2,1.1-2.7,2l-11.9,15.2c-1.6,2.1.3,5.1,2.9,4.4,41.9-10.1,77.7-36,100.8-71,3.8-5.7-.5-13.4-7.3-13.4h0c-3,0-5.7,1.5-7.3,4Z"
              />
              <path
                fill="#8e162e"
                d="M92.2,138.1c17.1-28.9,43.6-51.7,75.3-64,3.8-1.5,7.1-4,9.6-7.2l8.9-11.4c1.6-2.1-.3-5.1-2.9-4.5-45.1,10.6-83.1,39.5-106,78.3-3.4,5.8.8,13.1,7.5,13.1h0c3.1,0,5.9-1.7,7.5-4.4Z"
              />
            </g>
            <g>
              <path
                fill="#bd274a"
                d="M223.8,178.9l74.9-48.2-50-64.1-15.1-19.4-1.3-1.7c-2.6-3.3-6.4-5-10.3-5-3.8,0-7.6,1.7-10.2,5l-1.3,1.6-15.1,19.3-50.7,64.7,74.3,47.8c1.5,1,3.4,1,4.9,0Z"
              />
              <path
                fill="#bd274a"
                d="M417.7,212.1l-31.7-30.9-20.3-19.8c-1.2-1.2-2.7-1.9-4.3-2.1-.4,0-.7-.1-1.1-.1-2.1,0-4.2.9-5.8,2.6l-16.8,19-32.1-41.1-69.7,60c-1.3,1.1-2.2,2.6-2.3,4.3l-8.5,109.6c-.1,1.8-1.7,3.3-3.5,3.3s-3.4-1.4-3.5-3.3l-9.1-109.7c-.1-1.7-1-3.3-2.3-4.3l-69.1-59.6-32,40.8-16.9-19.1c-1.5-1.7-3.6-2.6-5.8-2.6-.4,0-.8,0-1.1.1-1.5.2-3,.9-4.2,2.1l-20.3,19.8-31.7,30.9c-3.1,3-3.1,8,0,11l33.6,32.7,18.4,18c1.5,1.5,3.4,2.2,5.4,2.2s1.1,0,1.6-.2c1.6-.3,3-1.1,4.2-2.4l19.6-22.2,102.1,130.3,1.3,1.6c2.6,3.3,6.4,5,10.2,5s7.7-1.7,10.3-5l1.3-1.7,101.5-130.2,19.5,22c1.1,1.3,2.6,2.1,4.1,2.4.5.1,1.1.2,1.6.2,1.9,0,3.9-.7,5.4-2.2l18.4-17.9,33.6-32.8c3.1-3,3.1-8,0-11Z"
              />
            </g>
          </svg>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-she-venoso-900 mb-2">
            SHE Heart Lab
          </h1>
          <p className="text-gray-700">
            Ambiente de desenvolvimento e pesquisa em saúde cardiovascular
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-she-palido-200 border-2 border-she-arterial-300 rounded-lg text-she-venoso-800 text-sm">
            {error}
          </div>
        )}

        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 bg-white border-2 border-she-palido-300 text-she-venoso-800 py-4 rounded-lg font-medium hover:bg-she-palido-50 hover:border-she-arterial-400 focus:outline-none focus:ring-2 focus:ring-she-arterial-500 focus:ring-offset-2 transition disabled:bg-gray-100 disabled:cursor-not-allowed shadow-sm"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span className="text-base">{loading ? 'Entrando...' : 'Entrar com Google'}</span>
        </button>

        <div className="mt-8 text-center text-sm text-gray-700">
          <p className="font-medium mb-1 text-she-venoso-800">Acesso restrito à equipe SHE</p>
          <p className="text-xs text-gray-600">Ambiente interno para aplicações de pesquisa e desenvolvimento</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
